import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {
  Text, Button, Chip, ActivityIndicator,
  Dialog, Portal,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {userApi} from '../../services/api/userApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const DetailRow = ({label, value, icon}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <View style={styles.detailLabelContainer}>
        {icon && <Icon name={icon} size={16} color={colors.textSecondary} style={{marginRight: 4}} />}
        <Text style={styles.detailLabel}>{label}</Text>
      </View>
      <Text style={styles.detailValue}>{value}</Text>
    </View>
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const UserDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {userId} = route.params;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await userApi.getById(userId);
      setUser(res.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, t]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleDeactivate = useCallback(async () => {
    setDeactivateVisible(false);
    try {
      await userApi.delete(userId);
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, navigation, t]);

  const handleReactivate = useCallback(async () => {
    try {
      await userApi.reactivate(userId);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, fetchData, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={user?.fullName || ''}
        onBack={() => navigation.goBack()}>
        <TouchableOpacity onPress={() => navigation.navigate('UserForm', {userId})} style={{padding: 8}}>
          <Icon name="pencil" size={22} color={colors.primary} />
        </TouchableOpacity>
      </GradientHeader>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {user && (
          <>
            {/* Avatar & Name */}
            <View style={styles.avatarSection}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{(user.fullName || '?').charAt(0).toUpperCase()}</Text>
              </View>
              <Text style={styles.name}>{user.fullName}</Text>
              <View style={styles.badgeRow}>
                <Chip
                  style={[styles.statusChip, {backgroundColor: user.isActive ? colors.success + '15' : colors.error + '15'}]}
                  compact>
                  <Text style={{fontSize: 12, fontWeight: '600', color: user.isActive ? colors.success : colors.error, marginTop: 2}}>
                    {user.isActive ? t('user.active') : t('user.inactive')}
                  </Text>
                </Chip>
                {user.isOwner && (
                  <Chip style={styles.ownerChip} compact>
                    <Text style={{fontSize: 12, fontWeight: '600', color: colors.warning, marginTop: 2}}>
                      {t('user.owner')}
                    </Text>
                  </Chip>
                )}
              </View>
            </View>

            {/* Personal Information */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('user.details')}</Text>
              <DetailRow icon="email-outline" label={t('user.email')} value={user.email} />
              <DetailRow icon="phone-outline" label={t('user.phone')} value={user.phone ? `${user.countryCode || '+91'} ${user.phone}` : null} />
              <DetailRow icon="store" label="Primary Store" value={user.primaryOrgUnitName} />
              <DetailRow icon="clock-outline" label="Last Login" value={formatDateTime(user.lastLoginAt)} />
              <DetailRow icon="calendar-plus" label="Created" value={formatDate(user.createdAt)} />
            </View>

            {/* Linked Worker */}
            {user.linkedWorkerName && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('user.linkedWorker')}</Text>
                <TouchableOpacity
                  style={styles.workerLink}
                  activeOpacity={0.7}
                  onPress={() => navigation.navigate('Workers', {
                    screen: 'WorkerDetail',
                    params: {workerId: user.linkedWorkerId},
                  })}>
                  <View style={styles.workerLinkLeft}>
                    <View style={styles.workerAvatar}>
                      <Icon name="account-wrench" size={20} color={colors.white} />
                    </View>
                    <Text style={styles.workerLinkName}>{user.linkedWorkerName}</Text>
                  </View>
                  <Icon name="chevron-right" size={22} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Org & Role Assignments */}
            {user.orgRoleAssignments && user.orgRoleAssignments.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('user.orgRoleAssignments')}</Text>
                {user.orgRoleAssignments.map((a, idx) => (
                  <View key={`${a.orgUnitId}-${a.roleId}-${idx}`} style={styles.storeRow}>
                    <View style={styles.storeInfo}>
                      <Icon name="store" size={16} color={colors.primary} />
                      <View style={{flex: 1}}>
                        <Text style={styles.storeName}>{a.orgUnitName}</Text>
                        <Text style={styles.storeRole}>{a.roleName}</Text>
                      </View>
                    </View>
                    {a.isPrimary && (
                      <Chip style={styles.primaryChip} compact>
                        <Text style={{fontSize: 11, fontWeight: '600', color: colors.primary, marginTop: 2}}>
                          {t('user.primary')}
                        </Text>
                      </Chip>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Notes */}
            {user.notes ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t('user.notes')}</Text>
                <Text style={styles.notesText}>{user.notes}</Text>
              </View>
            ) : null}

            {/* Action Buttons */}
            {!user.isOwner && (
              <View style={styles.actionRow}>
                {user.isActive ? (
                  <Button
                    mode="outlined"
                    icon="account-off"
                    textColor={colors.error}
                    style={styles.deactivateButton}
                    onPress={() => setDeactivateVisible(true)}>
                    {t('user.deactivate')}
                  </Button>
                ) : (
                  <Button
                    mode="outlined"
                    icon="account-check"
                    textColor={colors.success}
                    style={styles.reactivateButton}
                    onPress={handleReactivate}>
                    {t('user.reactivate')}
                  </Button>
                )}
              </View>
            )}
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('user.deactivateMessage', {name: user?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('user.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('user.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {snackbar ? (
        <View style={styles.snackbarContainer}>
          <Text style={styles.snackbarText}>{snackbar}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingBottom: spacing.md},

  // Avatar
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: colors.secondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  avatarText: {color: colors.white, fontSize: 32, fontWeight: '700'},
  name: {fontSize: 20, fontWeight: '700', color: colors.text},
  badgeRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  statusChip: {height: 26},
  ownerChip: {
    backgroundColor: colors.warning + '15',
    height: 26,
  },

  // Sections
  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Detail rows
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  detailLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
    marginLeft: spacing.md,
  },

  // Notes
  notesText: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 20,
  },

  // Linked worker
  workerLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  workerLinkLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  workerAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.secondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  workerLinkName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
    textDecorationLine: 'underline',
  },

  // Store rows
  storeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  storeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  storeName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  storeRole: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 1,
  },
  primaryChip: {
    backgroundColor: colors.primary + '15',
    height: 24,
  },

  // Actions
  actionRow: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  deactivateButton: {borderColor: colors.error, borderRadius: 8},
  reactivateButton: {borderColor: colors.success, borderRadius: 8},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
  snackbarContainer: {
    position: 'absolute', bottom: spacing.md, left: spacing.md, right: spacing.md,
    backgroundColor: '#333', borderRadius: 8, padding: spacing.md,
  },
  snackbarText: {color: '#fff', fontSize: 14},
});

export default UserDetailScreen;
